import type { DeploymentIntent, PackagePublicTerms, SignupCredit } from '@/lib/registrationIntent'

export const TERMS_VERSION = 'terms-2026-09-01-v1'
export const TERMS_CONTENT_HASH = '1179d4a06e16f6c4054089d0a56b5f57df18b9193c5cca31419b1c1d8bfeabbb'
export const NON_PAYMENT_POLICY_VERSION = 'non-payment-2026-09-01-v1'
export const NON_PAYMENT_POLICY_CONTENT_HASH = '8e5e7952a04eba5796616b02fc9a133b60b8946c1fd0d8227b9f82089f36ecd2'
export const TERMS_URL = `https://metrly.com/legal/terms/${TERMS_VERSION}`
export const NON_PAYMENT_POLICY_URL = `https://metrly.com/legal/non-payment/${NON_PAYMENT_POLICY_VERSION}`
export const PACKAGE_RECURRING_AUTHORIZATION_TEXT = 'I authorize Metrly to renew this package after the free trial and charge my payment method between the disclosed monthly minimum and monthly cap until I cancel.'

export interface PolicyConsentIdentity {
  consentBundleUuid: string
  registrationRecordUuid: string
}

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16)
    const value = character === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export function createPolicyConsentIdentity(): PolicyConsentIdentity {
  return { consentBundleUuid: randomUuid(), registrationRecordUuid: randomUuid() }
}

export interface PackageRenewalOffer {
  server_uuid: string
  display_name: string
  currency: string
  minimum_minor: number
  ceiling_minor: number
  assignment_id: number
  package_version_id: number
  public_terms?: PackagePublicTerms
}

export function buildPackageRenewalAcceptance(offer: PackageRenewalOffer): Record<string, unknown> {
  const identity = createPolicyConsentIdentity()
  return {
    server_uuid: offer.server_uuid,
    policy_acceptance: {
      consent_bundle_uuid: identity.consentBundleUuid,
      registration_record_uuid: identity.registrationRecordUuid,
      terms_accepted: true,
      non_payment_policy_acknowledged: true,
      recurring_billing_authorized: true,
      recurring_authorization_text: PACKAGE_RECURRING_AUTHORIZATION_TEXT,
      terms_version: TERMS_VERSION,
      terms_content_hash: TERMS_CONTENT_HASH,
      non_payment_policy_version: NON_PAYMENT_POLICY_VERSION,
      non_payment_policy_content_hash: NON_PAYMENT_POLICY_CONTENT_HASH,
      displayed_package: {
        package_version_id: offer.package_version_id,
        currency: offer.currency,
        minimum_minor: offer.minimum_minor,
        ceiling_minor: offer.ceiling_minor,
        ...(offer.public_terms?.recovery && offer.public_terms?.backups ? {
          disclosures: { recovery: offer.public_terms.recovery, backups: offer.public_terms.backups },
        } : {}),
      },
      customer_locale: typeof navigator === 'undefined' ? 'en-US' : navigator.language,
      currency: offer.currency,
      displayed_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
  }
}

export function buildPolicyAcceptance(input: {
  identity: PolicyConsentIdentity
  termsAccepted: boolean
  recurringBillingAuthorized: boolean
  intent: DeploymentIntent | null
  estimateLabel: string | null
  signupCredit: SignupCredit | null
  signupCreditLabel: string | null
  selectedServiceName?: string
  availableCreditLabel?: string
}): Record<string, unknown> {
  const selectedPackage = input.intent?.selection?.package || null
  const estimate = input.intent?.selection?.estimate || null
  const currency = String(input.intent?.selection?.package?.currency || estimate?.currency || input.signupCredit?.currency || 'usd').toLowerCase()
  const serviceName = input.selectedServiceName || input.intent?.selection?.name || input.intent?.selection?.game_slug || 'No paid service selected during account registration'
  const estimatedMonthlyRange = input.estimateLabel
    ? `${input.estimateLabel}; actual charges may range from $0 to a higher amount based on measured usage.`
    : 'No paid service selected at registration; $0 until a paid service is created, then actual monthly charges vary with measured usage.'

  const common = {
    consent_bundle_uuid: input.identity.consentBundleUuid,
    registration_record_uuid: input.identity.registrationRecordUuid,
    terms_accepted: input.termsAccepted,
    non_payment_policy_acknowledged: input.termsAccepted,
    recurring_billing_authorized: input.recurringBillingAuthorized,
    terms_version: TERMS_VERSION,
    terms_content_hash: TERMS_CONTENT_HASH,
    non_payment_policy_version: NON_PAYMENT_POLICY_VERSION,
    non_payment_policy_content_hash: NON_PAYMENT_POLICY_CONTENT_HASH,
    customer_locale: typeof navigator === 'undefined' ? 'en-US' : navigator.language,
    currency,
    displayed_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  }

  if (selectedPackage) {
    return {
      ...common,
      recurring_authorization_text: PACKAGE_RECURRING_AUTHORIZATION_TEXT,
      displayed_package: {
        package_version_id: selectedPackage.package_version_id,
        currency: selectedPackage.currency,
        minimum_minor: selectedPackage.pricing.minimum_minor,
        ceiling_minor: selectedPackage.pricing.ceiling_minor,
        ...(input.intent?.selection.package_terms?.recovery && input.intent.selection.package_terms.backups ? {
          disclosures: {
            recovery: input.intent.selection.package_terms.recovery,
            backups: input.intent.selection.package_terms.backups,
          },
        } : {}),
      },
    }
  }

  return {
    ...common,
    recurring_billing_disclosure_snapshot: {
      authorization_text: 'I authorize Metrly to automatically renew my services and charge my payment method for usage-based charges and other disclosed recurring fees until I cancel. I understand that the amount charged may vary based on usage.',
      automatic_renewal: true,
      variable_usage_charges: true,
    },
    pricing_disclosure_snapshot: {
      selected_service: serviceName,
      billing_cadence: 'Usage is measured hourly, aggregated monthly, and invoiced on the billing date displayed in the Metrly Console.',
      pricing_basis: 'Metered CPU, memory, storage, and transfer at the current rates disclosed at https://metrly.com/pricing.',
      estimated_monthly_range: estimatedMonthlyRange,
      available_credit: input.availableCreditLabel || input.signupCreditLabel || 'No promotional credit displayed',
      cancellation_method: 'Cancel the affected server from its Settings page in the Metrly Console; cancellation stops future server usage after the confirmed cutoff but does not erase accrued charges. Kept backups continue to incur storage charges until confirmed deletion in the Backup Vault.',
    },
  }
}
